import { ActionText } from '@components/action-text';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Header } from '@components/header';
import { IconApi } from '@components/icons/api';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconReadMore } from '@components/icons/read-more';
import { IconSSO } from '@components/icons/sso';
import { IconTrash } from '@components/icons/trash';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUserApps } from 'api/authentication/hooks';
import { UserApp } from 'api/authentication/requests';

import { useUserAppsRemoveModal } from './user-apps-remove-modal';
import { useUserAppsSidebar } from './user-apps-sidebar';

export const UserApps = () => {
  const { t } = useScopedTranslate(locale);
  const showSidebar = useUserAppsSidebar();
  const userAppQuery = useUserApps();
  const apps = userAppQuery?.data ?? [];

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: t('yourApiKeys') }]} />}
        actions={
          <Stack space={2} orientation="horizontal">
            <Button
              variant="secondary"
              text={t('documentation')}
              href={'https://docs.50skills.app/journeys/'}
              target="_blank"
              iconLeft={<IconReadMore />}
            />

            <Button
              variant="primary"
              text={t('addNewUserApp')}
              iconLeft={<IconSSO />}
              onClick={() => showSidebar()}
            />
          </Stack>
        }
      />
      <PageContent>
        <PageContainers.MiddleColumnPage>
          <Stack space={6}>
            <Stack space={2}>
              <Text font="bold18" text={t('fiftyApi')} />
              <div>
                <Text font="regular14" text={t('activeDevelopment')} color="textSecondary" />
                <ActionText.ExternalLink
                  font="regular14"
                  color="textSecondary"
                  href={'https://docs.50skills.app/journeys/'}
                  underline
                >
                  {t('apiDocumentation')}
                </ActionText.ExternalLink>
              </div>
            </Stack>
            <Separator />
            <Stack space={4}>
              <Text font="bold18" text={t('apiKeys')} />
              {userAppQuery.isLoading ? (
                <RelativeSpinner />
              ) : apps.length === 0 ? (
                <EmptyState />
              ) : (
                <Stack space={2}>
                  {apps.map((app) => (
                    <UserAppCard key={app.clientId} app={app} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </PageContainers.MiddleColumnPage>
      </PageContent>
    </>
  );
};

type UserAppCardProps = {
  app: UserApp;
};

const EmptyState = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Card space={6}>
        <Stack space={4} orientation="horizontal" align="center">
          <IconApi color="elementPurple60" />
          <Text font="semibold14" color="textSecondary" text={t('noApiKeys')} />
        </Stack>
      </Card>
      <Text font="regular14" text={t('createAPIKeys')} color="textSecondary" />
    </>
  );
};

const UserAppCard = ({ app }: UserAppCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showModal = useUserAppsRemoveModal();
  const showSidebar = useUserAppsSidebar();

  return (
    <Card space={4}>
      <Stack space={2} orientation="horizontal" justify="space-between" align="center">
        <Stack space={2} orientation="horizontal" align="center">
          <IconSSO size={32} color="elementPurple80" />
          <div>
            <Text font="semibold14" text={app.clientName} />
            <Text
              font="regular14"
              color={app.description ? 'textSecondary' : 'textHelper'}
              text={app.description || t('keyDescription')}
            />
          </div>
        </Stack>
        <Stack space={2} orientation="horizontal" align="center">
          <Tag
            text={app.hasFullAccess ? t('fullAccess') : t('customAccess')}
            variant={app.hasFullAccess ? 'green' : 'orange'}
            size="large"
          />
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconLeft: <IconMoreVertical />,
            }}
          >
            <Menu.Item
              onSelect={() => showSidebar({ userApp: app })}
              label={t('edit')}
              icon={<IconEdit />}
            />
            <Menu.Item
              onSelect={() => showModal({ clientId: app.clientId })}
              label={t('remove')}
              icon={<IconTrash />}
              variant="danger"
            />
          </Menu>
        </Stack>
      </Stack>
    </Card>
  );
};

export const locale = {
  en: {
    yourApiKeys: 'Your API keys',
    fiftyApi: '50skills API',
    activeDevelopment: '50skills API is under active development.',
    apiDocumentation: 'API Documentation',
    documentation: 'Documentation',
    apiKeys: 'API keys',
    createAPIKeys:
      'Create API keys for accessing and authenticating with the 50skills external API',
    addNewUserApp: 'Create API key',
    edit: 'Edit',
    remove: 'Remove API key',
    keyDescription: 'No description',
    fullAccess: 'Full access',
    customAccess: 'Custom access',
    noApiKeys: 'You do not have any API keys',
  },
};
